<script>
  import ForwardNetwork from "./ForwardNetwork.svelte";
  import { onMount } from "svelte";
  import {
    drawActivation,
    network,
    showLayerLine,
    labels,
    showSubScript,
    stepIndex,
    hideWeights,
  } from "../../store";
  import katexify from "../../katexify";
  import { tooltip } from "../../tooltip";

  $stepIndex = 0;
  $network = [2, 1, 1];

  // $: console.log("stepIndex", $stepIndex);

  const target2event = {
    0: () => {
      $showLayerLine = false;
      $network = [2, 1, 1];
      $labels = ["input", "function", "output"];
      $stepIndex = 0;
      $showSubScript = false;
      $drawActivation = false;
      $hideWeights = 2;
    },
    1: () => {
      $showLayerLine = false;
      $network = [2, 1, 1];
      $labels = ["X", "linear", "y"];
      $showSubScript = true;
      $drawActivation = false;
      $stepIndex = 1;
      $hideWeights = 2;
    },
    2: () => {
      $stepIndex = 2;
      $showLayerLine = false;
      $labels = ["X", "linear", "y"];
      $network = [2, 1, 1];
      $drawActivation = false;
      $hideWeights = 2;
    },
    3: () => {
      $stepIndex = 3;
      $labels = ["X", "sigmoid", "y"];
      $showLayerLine = false;
      $network = [2, 1, 1];
      $drawActivation = false;
      $hideWeights = 2;
    },
    4: () => {
      $stepIndex = 4;
      $showLayerLine = false;
      $drawActivation = false;
      $labels = ["X", "step", "y"];
      $network = [2, 1, 1];
      $hideWeights = 2;
    },
    5: () => {
      $stepIndex = 5;
      $showLayerLine = false;
      $drawActivation = true;
      $labels = ["X", "step", "y"];
      $network = [2, 1, 1];
      $hideWeights = 2;
    },
    6: () => {
      $stepIndex = 6;
      $showLayerLine = false;
      $labels = ["X", "step", "step", "y"];
      $network = [2, 1, 1, 1];
      $drawActivation = false;
      $hideWeights = 3;
    },
    7: () => {
      $stepIndex = 7;
      $showLayerLine = true;
      $labels = ["X", "step", "step", "y"];
      $network = [2, 1, 1, 1];
      $drawActivation = false;
      $hideWeights = 3;
    },
    8: () => {
      $stepIndex = 8;
      $showLayerLine = true;
      $labels = ["X", "reLu", "sigmoid", "y"];
      $network = [2, 3, 2, 1];
      $drawActivation = false;
      $hideWeights = 12;
    },
  };

  function fireEvent(entryIndex) {
    if (entryIndex in target2event) {
      target2event[entryIndex]();
    }
  }

  onMount(() => {
    // store elements to track
    const sections = [...document.querySelectorAll(".step")];
    // observe elements to track
    sections.forEach((section) => {
      observer.observe(section);
    });
  });

  // options for intersection observer
  const options = {
    threshold: 0.7,
  };

  let observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      // check if visible or not
      if (entry.isIntersecting) {
        // resolve stage in graph
        const entryIndex = entry.target.getAttribute("data-index");
        if (entryIndex in target2event) {
          fireEvent(entryIndex);
        }
      }
    });
  }, options);

  const sigmoid =
    "\\begin{aligned} \\text{sigmoid} = \\frac{1}{1+e^{-w_iX_i}} \\end{aligned}";
  const linearEq = `\\begin{aligned} \\text{linear} = \\sum^{n}_{i=1}w_iX_i \\end{aligned}`;
  const stepEq = `\\begin{aligned} \\text{step} = \\begin{cases}
         +1, \\text{if } w_iX_i \\geq 0  \\\\
         -1, \\text{if } w_iX_i < 0
        \\end{cases} \\end{aligned}`;
  const eq = `y=w_0+ w_1X_1  + w_2X_2`;
  const eq1 = `y=w_0+ w_1X_1 `;
</script>

<!-- <h1 class='main-title'>Neural Networks</h1> -->
<section>
  <div class="scrolly-container">
    <div class="charts-container">
      <div class="chart-holder">
        <ForwardNetwork />
      </div>
    </div>
    <div class="steps-container">
      <div class="step" data-index="0">
        <div class="step-content">
            <h2>What is a Computational Graph?</h2>
            <hr />
            <br />
            <p>
              Imagine a simple flow of information. First, you put your data into the graph (input node). Next, the data goes through a function that does something to it (function node). Finally, you get the answer out (output node).
              <br /><br />
              The data always moves in one direction: from input, through the function, to the output.
            </p>
        </div>
      </div>
      <div class="step" data-index="1">
        <div class="step-content">
            <h2>Linear Regression</h2>
            <hr />
            <br />
            <p>
              We can use computational graphs to show how different math models work. For example, let's look at Linear Regression:
              {@html katexify(eq1, true)}
              To make this graph, we do two things:
              <br /><br />
              1. Add weights to the lines connecting the input to the function. (A weight is just a number that changes how much each input matters.)<sup><span class="info-tooltip" title="One of the X terms is just 1, for the bias." use:tooltip>[&#8505;]</span></sup>
              <br /><br />
              2. Change the function so it adds up all the weighted inputs:
              {@html katexify(linearEq, true)}
              Now, we have a simple graph for linear regression!
            </p>
        </div>
      </div>
      <div class="step" data-index="2">
        <div class="step-content">
            <h2>Model Outputs</h2>
            <hr />
            <br />
            <p>
              Let's see what the model gives us as output.
              <br /><br />
              The model makes predictions (shown as a yellow line) using the input data.
              <br /><br />
              As we make our models more complex, the output will change to match what the new model does.
            </p>
        </div>
      </div>
      <div class="step" data-index="3">
        <div class="step-content">
            <h2>Logistic Regression</h2>
            <hr />
            <br />
            <p>
              What if we want to solve a different problem? We can change our model from linear regression to logistic regression.
              <br /><br />
              We do this by changing the function in our graph to a sigmoid function:
              {@html katexify(sigmoid, true)}
              Now, the model output (the colored background) shows how the model makes decisions for different inputs.
            </p>
        </div>
      </div>
      <div class="step" data-index="4">
        <div class="step-content">
            <h2>Perceptrons</h2>
            <hr />
            <br />
            <p>
              We can use our graph to show other models too. For example, a Perceptron is like logistic regression, but uses a step function instead:
              {@html katexify(stepEq, true)}
              Notice how the output changes to match this new model.
            </p>
        </div>
      </div>
      <div class="step" data-index="5">
        <div class="step-content">
            <h2>Activation Functions & Artificial Neurons</h2>
            <hr />
            <br />
            <p>
              In a neural network, the function node is special. We call it an <span class="bold">artificial neuron</span>.
              <br /><br />
              An artificial neuron takes in numbers, multiplies them by weights, adds them up, and then uses a function to decide the output.
              <br /><br />
              In neural networks, this function is called an <span class="bold">activation function</span>. It must be non-linear (not just a straight line) so the network can learn more complex things.
            </p>
        </div>
      </div>
      <div class="step" data-index="6">
        <div class="step-content">
            <h2>Neural Networks</h2>
            <hr />
            <br />
            <p>
              We can connect many artificial neurons together, one after another. <span class="bold">This is what a neural network is!</span>
              <br /><br />
              The first neural networks were called <span class="bold">multilayer perceptrons</span> because they had layers of these simple neurons (using step functions) connected together.
            </p>
        </div>
      </div>
      <div class="step" data-index="7">
        <div class="step-content">
            <h2>Architecture</h2>
            <hr />
            <br />
            <p>
              A neural network is made of three types of layers:
              <br /><br /><span class="bold">Input layer</span>: This is where the data comes in. There is one node for each input.
              <br /><br /><span class="bold">Hidden layer(s)</span>: These are in the middle. They have artificial neurons that do the work.
              <br /><br /><span class="bold">Output layer</span>: This is where the answer comes out.
              <br /><br />
              There is only one input and one output layer, but you can have as many hidden layers as you want.
            </p>
        </div>
      </div>

      <div class="step" data-index="8">
        <div class="step-content">
            <h2>No Limits</h2>
            <hr />
            <br />
            <p>
              There are many ways to design a neural network. The input and output layers depend on your problem, but you can choose how many hidden layers and neurons you want.
              <br /><br />
              Neural networks can be <span class="bold">wide</span> (many neurons in a layer) or <span class="bold">deep</span> (many hidden layers). More neurons and layers can help the network learn more, but can also make it slower and sometimes too complex.
            </p>
        </div>
      </div>
    </div>
  </div>
</section>

<style>
  section {
    padding-bottom: 5rem;
    max-width: 1200px;
    margin: auto;
  }

  .scrolly-container {
    text-align: center;
    display: flex;
    margin-top: 0;
    padding-top: 0;
    flex-direction: row;
    padding-left: 1rem;
    width: 88%;
    margin: auto;
  }

  .charts-container {
    position: sticky;
    top: 25%;
    width: 100%;
    height: 45vh;
    margin-right: 2%;
    flex: 0 0 60%; /* This item will take up 70% of the container's width */
  }

  .steps-container {
    /* border: 2px solid red; */
    width: 100%;
  }

  .chart-holder {
    width: 100%;
    height: 100%;
  }

  .step {
    position: relative;
    scroll-snap-align: center;
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    height: 90vh;
    display: flex;
    place-items: center;
    justify-content: flex-start;
    padding: 1rem;
    padding-right: 1.4rem;
  }
  .step {
    height: 100vh;
  }

  .step-content {
    text-align: left;
    font-size: var(--size-default);
    font-family: var(--font-light);
    padding-left: 1.5rem;
    background-color: var(--white);
    border: 5px solid var(--squidink);
    padding: 20px;
  }

  .step-content > h2 {
    font-family: var(--font-heavy);
    text-transform: uppercase;
    font-size: var(--size-default);
  }

  .step-content > p {
    font-size: var(--size-default);
    font-family: var(--font-light);
  }

  hr {
    width: 100%;
  }

  /* .step > * {
      opacity: 1;
      transform: translate3d(0, 4rem, 0);
      transition: opacity 800ms var(--delay),
        transform 800ms cubic-bezier(0.13, 0.07, 0.26, 0.99) var(--delay);
    } */

  /* Comment out the following line to always make it 'text-on-top' */
  @media screen and (max-width: 950px) {
    .scrolly-container {
      display: block;
      top: 30%;
      width: 99%;
      padding-left: 0;
    }

    .step {
      height: 120vh;
      padding: 0;
      padding-right: 0;
      margin-bottom: 50vh;
    }

    .step-content {
      text-align: left;
      font-size: var(--size-default);
      font-family: var(--font-light);
      padding-left: 0;
      background-color: rgba(255, 255, 255, 0.93);
      border: 3px solid var(--squidink);
      padding: 10px;
      line-height: 1.1;
    }
    .steps-container {
      pointer-events: none;
    }
    .charts-container {
      top: 25%;
      width: 100%;
      height: 45vh;
      margin-right: 0%;
      flex: 0 0 100%;
    }
  }
  /* 
  @media screen and (max-width: 950px) {
    .scrolly-container {
      display: block;
      flex-direction: column-reverse;
      top: 30%;
      width: 99%;
      padding-left: 0;
    }

    .steps-container {
      pointer-events: none;
    }

    .charts-container {
      top: 20.5%;
      width: 95%;
      margin: auto;
      top: 25%;
      width: 100%;
      height: 45vh;
      margin-right: 0%;
    }

    .step {
      height: 130vh;
    }

    .step-content {
      width: 95%;
      max-width: 768px;
      font-size: 15px;
      line-height: 1.3;
      background: rgba(241, 243, 243, 0.913);
    }
    .charts-container {
      top: 25%;
      width: 100%;
      height: 45vh;
      margin-right: 0%;
      flex: 0 0 100%;
    }
  } */
</style>
